/**
 * Documentos de habilitação (17 §3.1; D63) e atestados de capacidade técnica (D64).
 * - Documentos: visão do pacote atual por grupo, com situação calculada (vigente, vence em 15/3 dias, vencido).
 *   Renovar = enviar a nova versão; a anterior fica no histórico. Envio em lote com tipo e validade sugeridos pelo nome do arquivo.
 * - Atestados: a solicitar → solicitado (com requerimento, notas fiscais, proposta) → recebido (com o PDF do atestado).
 */
import { useMemo, useState } from "react";
import {
  AutocompleteInput,
  Create,
  CreateButton,
  TopToolbar,
  Datagrid,
  DateInput,
  Edit,
  FunctionField,
  List,
  ReferenceField,
  ReferenceInput,
  SelectInput,
  SimpleForm,
  SimpleList,
  TextField,
  TextInput,
  Title,
  required,
  useGetList,
  useInput,
  useNotify,
  usePermissions,
  useRecordContext,
  useRefresh,
} from "react-admin";
import Alert from "@mui/material/Alert";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import Collapse from "@mui/material/Collapse";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextFieldMui from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/AddOutlined";
import AutorenewIcon from "@mui/icons-material/AutorenewOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/EditOutlined";
import FaltanteIcon from "@mui/icons-material/ErrorOutline";
import IncompletoIcon from "@mui/icons-material/WarningAmberOutlined";
import VerIcon from "@mui/icons-material/VisibilityOutlined";
import FolderIcon from "@mui/icons-material/FolderOpenOutlined";
import HistoryIcon from "@mui/icons-material/HistoryOutlined";
import OpenIcon from "@mui/icons-material/OpenInNewOutlined";
import UploadIcon from "@mui/icons-material/UploadFileOutlined";
import { BarraFormulario, useCelular, vazioParaNulo } from "../componentes";
import { CadastroRapidoPessoa, DialogoCadastroPessoa, criarPessoaPorCnpj } from "./orgaos";
import { formatarData, normalizarBusca } from "../lib/formato";
import { supabase } from "../lib/supabase";
import type { Permissoes } from "../lib/provedores";
import { ZonaArquivo } from "./entradas-produto";
import { textoDoPdf } from "../lib/pdf";
import { extrairCnpjs, extrairDataAtestado, extrairDatasDocumento, formatarCnpj } from "@sobral/dominio";

const BUCKET = "habilitacao";
const TIPOS_ACEITOS = [
  "application/pdf",
  "image/png",
  "image/jpeg",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

export const GRUPOS = [
  { id: "juridica", name: "Habilitação jurídica" },
  { id: "fiscal", name: "Regularidade fiscal, social e trabalhista" },
  { id: "economica", name: "Qualificação econômico-financeira" },
  { id: "tecnica", name: "Qualificação técnica" },
  { id: "declaracao", name: "Declarações" },
] as const;

type Grupo = (typeof GRUPOS)[number]["id"];

interface TipoDocumento {
  id: string;
  nome: string;
  grupo: Grupo;
  tem_validade: boolean;
  palavras_chave: string[];
  obrigatorio: boolean;
  ativo: boolean;
}

interface DocumentoVista {
  id: string;
  tipo_id: string;
  tipo_nome: string;
  grupo: Grupo;
  descricao: string | null;
  emissao: string | null;
  validade: string | null;
  arquivo_caminho: string;
  arquivo_nome: string | null;
  observacao: string | null;
  atual: boolean;
  dias_para_vencer: number | null;
  situacao: "sem_validade" | "vencido" | "vence_3_dias" | "vence_15_dias" | "vigente";
  criado_em: string;
}

/** Abre um arquivo do bucket privado com link temporário. */
export async function abrirArquivo(caminho: string) {
  const { data, error } = await supabase.storage.from(BUCKET).createSignedUrl(caminho, 300);
  if (error || !data) return;
  window.open(data.signedUrl, "_blank", "noopener");
}

/** Abre em outra aba um arquivo ainda não enviado (conferência nas listas de envio em lote). */
function verArquivoLocal(arquivo: File) {
  const url = URL.createObjectURL(arquivo);
  window.open(url, "_blank", "noopener");
  // A aba já carregou o arquivo; libera a memória depois.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

function BotaoVerArquivo({ arquivo }: { arquivo: File }) {
  return (
    <Tooltip title="Ver o arquivo">
      <IconButton size="small" onClick={() => verArquivoLocal(arquivo)} aria-label="Ver o arquivo">
        <VerIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
}

/** Dados que faltam num documento já enviado (emissão; validade quando o tipo tem validade). */
function dadosFaltantes(d: Pick<DocumentoVista, "emissao" | "validade">, tipo: Pick<TipoDocumento, "tem_validade">): string[] {
  const faltam: string[] = [];
  if (!d.emissao) faltam.push("emissão");
  if (tipo.tem_validade && !d.validade) faltam.push("validade");
  return faltam;
}

/** Envia um arquivo ao bucket e devolve o caminho gravado. */
async function enviarArquivo(pasta: string, arquivo: File): Promise<string> {
  const extensao = arquivo.name.split(".").pop()?.toLowerCase() ?? "pdf";
  const caminho = `${pasta}/${new Date().toISOString().replace(/[:.]/g, "-")}-${Math.random().toString(36).slice(2, 7)}.${extensao}`;
  const { error } = await supabase.storage.from(BUCKET).upload(caminho, arquivo, { ...(arquivo.type ? { contentType: arquivo.type } : {}), upsert: false });
  if (error) throw new Error(error.message);
  return caminho;
}

function ChipSituacaoDocumento({ d }: { d: Pick<DocumentoVista, "situacao" | "dias_para_vencer" | "validade"> }) {
  switch (d.situacao) {
    case "sem_validade":
      return <Chip size="small" variant="outlined" label="Sem validade" />;
    case "vencido":
      return <Chip size="small" color="error" label={`Vencido em ${formatarData(d.validade)}`} />;
    case "vence_3_dias":
      return <Chip size="small" color="error" label={d.dias_para_vencer === 0 ? "Vence hoje" : `Vence em ${d.dias_para_vencer} dia(s)`} />;
    case "vence_15_dias":
      return <Chip size="small" color="warning" label={`Vence em ${d.dias_para_vencer} dias`} />;
    default:
      return <Chip size="small" variant="outlined" color="success" label={`Válido até ${formatarData(d.validade)}`} />;
  }
}

/* -------------------------- Sugestões a partir do nome do arquivo -------------------------- */

/** Data no formato ddmmaaaa (ou dd.mm.aaaa / dd-mm-aaaa) no nome do arquivo → yyyy-mm-dd. */
function dataDoNome(nome: string): string | null {
  const m = nome.match(/(\d{2})[.\-_]?(\d{2})[.\-_]?(20\d{2})/);
  if (!m) return null;
  const [, dia, mes, ano] = m;
  const d = Number(dia);
  const ms = Number(mes);
  if (d < 1 || d > 31 || ms < 1 || ms > 12) return null;
  return `${ano}-${mes}-${dia}`;
}

/** Grupo pela pasta de origem ("1. Habilitação Jurídica", "2. … Fiscal …" etc.). */
function grupoDaPasta(caminho: string): Grupo | null {
  const t = normalizarBusca(caminho);
  if (t.includes("juridica")) return "juridica";
  if (t.includes("fiscal") || t.includes("trabalhista")) return "fiscal";
  if (t.includes("economica") || t.includes("financeira")) return "economica";
  if (t.includes("tecnica")) return "tecnica";
  if (t.includes("declarac")) return "declaracao";
  return null;
}

/** Tipo cujo conjunto de palavras-chave mais longo aparece no nome do arquivo (preferindo o grupo da pasta). */
function tipoDoNome(nome: string, grupo: Grupo | null, tipos: TipoDocumento[]): TipoDocumento | null {
  const n = normalizarBusca(nome);
  let melhor: { tipo: TipoDocumento; pontos: number } | null = null;
  for (const t of tipos) {
    for (const p of t.palavras_chave) {
      if (n.includes(p)) {
        const pontos = p.length + (grupo && t.grupo === grupo ? 100 : 0);
        if (!melhor || pontos > melhor.pontos) melhor = { tipo: t, pontos };
      }
    }
  }
  return melhor?.tipo ?? null;
}

interface LinhaLote {
  chave: string;
  arquivo: File;
  caminhoOrigem: string;
  tipo_id: string;
  emissao: string;
  validade: string;
  origemDatas: "pdf" | "nome" | null;
  lendo: boolean;
  enviar: boolean;
  /** Já existe documento deste tipo com o mesmo nome de arquivo (envio repetido). */
  jaEnviado: boolean;
}

/** Lê emissão e validade do texto do PDF; se não houver texto, usa a data do nome do arquivo (validade). */
async function datasSugeridas(arquivo: File, temValidade: boolean): Promise<{ emissao: string; validade: string; origem: "pdf" | "nome" | null }> {
  const texto = await textoDoPdf(arquivo);
  const lidas = texto ? extrairDatasDocumento(texto) : { emissao: null, validade: null };
  if (lidas.emissao || lidas.validade) return { emissao: lidas.emissao ?? "", validade: lidas.validade ?? "", origem: "pdf" };
  const doNome = dataDoNome(arquivo.name);
  if (doNome && temValidade) return { emissao: "", validade: doNome, origem: "nome" };
  return { emissao: "", validade: "", origem: null };
}

function MarcaOrigem({ origem, lendo }: { origem: LinhaLote["origemDatas"]; lendo: boolean }) {
  if (lendo) return <Typography variant="caption" color="text.secondary">Lendo o PDF…</Typography>;
  if (origem === "pdf") return <Typography variant="caption" color="success.main">Datas lidas do PDF</Typography>;
  if (origem === "nome") return <Typography variant="caption" color="text.secondary">Validade do nome do arquivo</Typography>;
  return <Typography variant="caption" color="warning.main">Preencha manualmente</Typography>;
}

/** Envio de vários documentos de uma vez: tipo pelo nome e pela pasta; datas lidas do PDF; conferência antes de gravar. */
function DialogoLote({
  aberto,
  aoFechar,
  tipos,
  documentos,
}: {
  aberto: boolean;
  aoFechar: () => void;
  tipos: TipoDocumento[];
  documentos: DocumentoVista[];
}) {
  const notify = useNotify();
  const refresh = useRefresh();
  const celular = useCelular();
  const [linhas, setLinhas] = useState<LinhaLote[]>([]);
  const [gravando, setGravando] = useState(false);
  const [progresso, setProgresso] = useState(0);

  const alterar = (chave: string, campos: Partial<LinhaLote>) =>
    setLinhas((ls) => ls.map((l) => (l.chave === chave ? { ...l, ...campos } : l)));

  function receber(lista: FileList | null) {
    if (!lista) return;
    const novas: LinhaLote[] = [];
    for (const arquivo of Array.from(lista)) {
      if (!TIPOS_ACEITOS.includes(arquivo.type)) continue;
      const caminho = (arquivo as File & { webkitRelativePath?: string }).webkitRelativePath || arquivo.name;
      const tipo = tipoDoNome(arquivo.name, grupoDaPasta(caminho), tipos);
      const jaEnviado = documentos.some((d) => d.arquivo_nome === arquivo.name && (!tipo || d.tipo_id === tipo.id));
      novas.push({
        chave: `${caminho}-${Math.random().toString(36).slice(2, 8)}`,
        arquivo,
        caminhoOrigem: caminho,
        tipo_id: tipo?.id ?? "",
        emissao: "",
        validade: "",
        origemDatas: null,
        lendo: true,
        // Arquivo já enviado antes fica desmarcado (evita versões repetidas no histórico).
        enviar: Boolean(tipo) && !jaEnviado,
        jaEnviado,
      });
    }
    setLinhas((atuais) => [...atuais, ...novas]);
    // Leitura das datas em sequência (não trava a tela).
    void (async () => {
      for (const l of novas) {
        const tipo = tipos.find((t) => t.id === l.tipo_id);
        const datas = await datasSugeridas(l.arquivo, tipo?.tem_validade ?? true);
        alterar(l.chave, { emissao: datas.emissao, validade: datas.validade, origemDatas: datas.origem, lendo: false });
      }
    })();
  }

  // Grava só as linhas prontas (tipo escolhido e leitura concluída); as pendentes ficam na lista.
  async function gravar() {
    // Ordem crescente de emissão: o mais recente de cada tipo é gravado por último e fica como atual (D69).
    const selecionadas = linhas
      .filter((l) => l.enviar && l.tipo_id && !l.lendo)
      .sort((a, b) => (a.emissao || "").localeCompare(b.emissao || "") || a.caminhoOrigem.localeCompare(b.caminhoOrigem));
    const pendentes = linhas.filter((l) => l.enviar && (!l.tipo_id || l.lendo)).length;
    if (selecionadas.length === 0) return;
    setGravando(true);
    let feitos = 0;
    const falhas: string[] = [];
    for (const l of selecionadas) {
      try {
        const caminho = await enviarArquivo("documentos", l.arquivo);
        const { error } = await supabase.from("documento_habilitacao").insert({
          tipo_id: l.tipo_id,
          emissao: l.emissao || null,
          validade: l.validade || null,
          arquivo_caminho: caminho,
          arquivo_nome: l.arquivo.name,
        });
        if (error) throw new Error(error.message);
        feitos += 1;
        setLinhas((ls) => ls.filter((x) => x.chave !== l.chave));
      } catch (e) {
        falhas.push(`${l.arquivo.name}: ${(e as Error).message}`);
      }
      setProgresso(Math.round(((feitos + falhas.length) / selecionadas.length) * 100));
    }
    setGravando(false);
    const aviso = [
      `${feitos} documento(s) gravado(s).`,
      pendentes ? `${pendentes} pendente(s) sem tipo continuam na lista.` : "",
      falhas.length ? `Não gravados: ${falhas.join("; ")}` : "",
    ].filter(Boolean);
    notify(aviso.join(" "), { type: falhas.length || pendentes ? "warning" : "success" });
    if (feitos > 0) refresh();
    if (falhas.length === 0 && pendentes === 0) aoFechar();
  }

  const lendo = linhas.some((l) => l.lendo);
  const marcadas = linhas.filter((l) => l.enviar && l.tipo_id && !l.lendo).length;
  const pendentesLista = linhas.filter((l) => l.enviar && !l.tipo_id).length;

  return (
    <Dialog open={aberto} onClose={gravando ? undefined : aoFechar} fullWidth maxWidth="xl" fullScreen={celular}>
      <DialogTitle>Enviar documentos em lote</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Alert severity="info">
            Selecione os arquivos ou a pasta do pacote. O tipo é sugerido pelo nome e pela pasta; emissão e validade são lidas do
            texto do PDF (certidões digitais). PDFs escaneados ficam para preenchimento manual. Confira antes de gravar.
          </Alert>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button component="label" variant="outlined" startIcon={<UploadIcon />} disabled={gravando}>
              Selecionar arquivos
              <input type="file" hidden multiple accept={TIPOS_ACEITOS.join(",")} onChange={(e) => receber(e.target.files)} />
            </Button>
            <Button component="label" variant="outlined" startIcon={<FolderIcon />} disabled={gravando}>
              Selecionar pasta
              <input type="file" hidden multiple onChange={(e) => receber(e.target.files)} {...({ webkitdirectory: "", directory: "" } as Record<string, string>)} />
            </Button>
          </Stack>
          {linhas.length > 0 && (
            <TableContainer sx={{ overflowX: "auto" }}>
              <Table size="small" sx={{ tableLayout: "fixed", minWidth: 900 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 56 }}>Enviar</TableCell>
                    <TableCell>Arquivo</TableCell>
                    <TableCell sx={{ width: 300 }}>Tipo</TableCell>
                    <TableCell sx={{ width: 165 }}>Emissão</TableCell>
                    <TableCell sx={{ width: 165 }}>Validade</TableCell>
                    <TableCell sx={{ width: 48 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {linhas.map((l) => (
                    <TableRow key={l.chave} sx={{ opacity: l.enviar ? 1 : 0.5, verticalAlign: "top" }}>
                      <TableCell>
                        <input type="checkbox" checked={l.enviar} onChange={(e) => alterar(l.chave, { enviar: e.target.checked })} aria-label="Enviar" />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0 }}>
                          <BotaoVerArquivo arquivo={l.arquivo} />
                          <Tooltip title={l.caminhoOrigem}>
                            <Typography variant="body2" noWrap>
                              {l.arquivo.name}
                            </Typography>
                          </Tooltip>
                        </Stack>
                        <MarcaOrigem origem={l.origemDatas} lendo={l.lendo} />
                        {l.jaEnviado && (
                          <Typography variant="caption" color="warning.main" display="block">
                            Já enviado antes: marque só se for uma nova versão.
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <TextFieldMui select size="small" value={l.tipo_id} onChange={(e) => alterar(l.chave, { tipo_id: e.target.value })} fullWidth error={l.enviar && !l.tipo_id}>
                          <MenuItem value="">Escolha o tipo</MenuItem>
                          {GRUPOS.map((g) => [
                            <MenuItem key={g.id} disabled sx={{ fontWeight: 700, opacity: "1 !important" }}>
                              {g.name}
                            </MenuItem>,
                            ...tipos
                              .filter((t) => t.grupo === g.id && t.ativo)
                              .map((t) => (
                                <MenuItem key={t.id} value={t.id} sx={{ pl: 3 }}>
                                  {t.nome}
                                </MenuItem>
                              )),
                          ])}
                        </TextFieldMui>
                      </TableCell>
                      <TableCell>
                        <TextFieldMui type="date" size="small" value={l.emissao} onChange={(e) => alterar(l.chave, { emissao: e.target.value })} fullWidth />
                      </TableCell>
                      <TableCell>
                        <TextFieldMui type="date" size="small" value={l.validade} onChange={(e) => alterar(l.chave, { validade: e.target.value })} fullWidth />
                      </TableCell>
                      <TableCell>
                        <IconButton size="small" onClick={() => setLinhas((ls) => ls.filter((x) => x.chave !== l.chave))} aria-label="Remover da lista">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Typography variant="caption" sx={{ mr: "auto", pl: 2 }}>
          {gravando
            ? `Gravando… ${progresso}%`
            : lendo
              ? "Lendo os PDFs…"
              : linhas.length > 0
                ? `${linhas.length} arquivo(s) na lista${pendentesLista ? ` · ${pendentesLista} sem tipo (ficam na lista)` : ""}`
                : ""}
        </Typography>
        <Button onClick={aoFechar} disabled={gravando}>
          Cancelar
        </Button>
        <Button variant="contained" onClick={() => void gravar()} disabled={gravando || marcadas === 0}>
          Gravar {marcadas} pronto(s)
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** Envio de um documento (novo ou renovação de um tipo). */
function DialogoDocumento({
  tipo,
  aoFechar,
  tipos,
}: {
  tipo: TipoDocumento | null | "novo";
  aoFechar: () => void;
  tipos: TipoDocumento[];
}) {
  const notify = useNotify();
  const refresh = useRefresh();
  const [tipoId, setTipoId] = useState("");
  const [emissao, setEmissao] = useState("");
  const [validade, setValidade] = useState("");
  const [descricao, setDescricao] = useState("");
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [gravando, setGravando] = useState(false);
  const [chave, setChave] = useState<string | null>(null);

  const chaveAtual = tipo === null ? null : tipo === "novo" ? "novo" : tipo.id;
  if (chaveAtual !== chave) {
    setChave(chaveAtual);
    setTipoId(tipo && tipo !== "novo" ? tipo.id : "");
    setEmissao("");
    setValidade("");
    setDescricao("");
    setArquivo(null);
  }
  if (tipo === null) return null;
  const tipoEscolhido = tipos.find((t) => t.id === tipoId);

  async function gravar() {
    if (!tipoId || !arquivo) return;
    setGravando(true);
    try {
      const caminho = await enviarArquivo("documentos", arquivo);
      const { error } = await supabase.from("documento_habilitacao").insert({
        tipo_id: tipoId,
        emissao: emissao || null,
        validade: validade || null,
        descricao: descricao.trim() || null,
        arquivo_caminho: caminho,
        arquivo_nome: arquivo.name,
      });
      if (error) throw new Error(error.message);
      notify("Documento gravado. A versão anterior, se houver, fica no histórico.", { type: "success" });
      refresh();
      aoFechar();
    } catch (e) {
      notify(`Não foi possível gravar: ${(e as Error).message}`, { type: "error" });
    } finally {
      setGravando(false);
    }
  }

  return (
    <Dialog open onClose={gravando ? undefined : aoFechar} fullWidth maxWidth="sm">
      <DialogTitle>{tipo === "novo" ? "Adicionar documento" : `Renovar: ${tipo.nome}`}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {tipo === "novo" && (
            <TextFieldMui select label="Tipo de documento" value={tipoId} onChange={(e) => setTipoId(e.target.value)} required fullWidth>
              {GRUPOS.map((g) => [
                <MenuItem key={g.id} disabled sx={{ fontWeight: 700, opacity: "1 !important" }}>
                  {g.name}
                </MenuItem>,
                ...tipos
                  .filter((t) => t.grupo === g.id && t.ativo)
                  .map((t) => (
                    <MenuItem key={t.id} value={t.id} sx={{ pl: 3 }}>
                      {t.nome}
                    </MenuItem>
                  )),
              ])}
            </TextFieldMui>
          )}
          <ZonaArquivo
            aceitos={TIPOS_ACEITOS}
            rotulo="Arquivo do documento"
            enviando={false}
            alturaMinima={110}
            aoReceber={(a) => {
              setArquivo(a);
              void datasSugeridas(a, tipoEscolhido?.tem_validade ?? true).then((d) => {
                if (d.emissao) setEmissao((atual) => atual || d.emissao);
                if (d.validade) setValidade((atual) => atual || d.validade);
              });
            }}
          >
            {arquivo && <Typography variant="body2">{arquivo.name}</Typography>}
          </ZonaArquivo>
          <Stack direction="row" spacing={2}>
            <TextFieldMui type="date" label="Emissão" value={emissao} onChange={(e) => setEmissao(e.target.value)} fullWidth InputLabelProps={{ shrink: true }} />
            <TextFieldMui
              type="date"
              label="Validade"
              value={validade}
              onChange={(e) => setValidade(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              helperText={tipoEscolhido && !tipoEscolhido.tem_validade ? "Este tipo normalmente não tem validade." : undefined}
            />
          </Stack>
          <TextFieldMui label="Descrição (opcional)" value={descricao} onChange={(e) => setDescricao(e.target.value)} fullWidth />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={aoFechar} disabled={gravando}>
          Cancelar
        </Button>
        <Button variant="contained" onClick={() => void gravar()} disabled={gravando || !tipoId || !arquivo}>
          {gravando ? "Gravando…" : "Gravar documento"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** Correção de um documento já enviado (tipo, emissão, validade, descrição). A alteração fica na auditoria. */
function DialogoEditarDocumento({ documento, tipos, aoFechar }: { documento: DocumentoVista | null; tipos: TipoDocumento[]; aoFechar: () => void }) {
  const notify = useNotify();
  const refresh = useRefresh();
  const [campos, setCampos] = useState({ tipo_id: "", emissao: "", validade: "", descricao: "" });
  const [chave, setChave] = useState<string | null>(null);
  const [gravando, setGravando] = useState(false);
  const [confirmarExclusao, setConfirmarExclusao] = useState(false);
  const { permissions } = usePermissions<Permissoes | null>();
  const admin = permissions?.papel === "admin";
  if ((documento?.id ?? null) !== chave) {
    setChave(documento?.id ?? null);
    setConfirmarExclusao(false);
    setCampos({
      tipo_id: documento?.tipo_id ?? "",
      emissao: documento?.emissao ?? "",
      validade: documento?.validade ?? "",
      descricao: documento?.descricao ?? "",
    });
  }
  if (!documento) return null;
  const tipo = tipos.find((t) => t.id === campos.tipo_id);
  const datasInvalidas = Boolean(campos.emissao && campos.validade && campos.validade < campos.emissao);

  async function gravar() {
    if (!documento) return;
    setGravando(true);
    const { error } = await supabase
      .from("documento_habilitacao")
      .update({
        tipo_id: campos.tipo_id,
        emissao: campos.emissao || null,
        validade: campos.validade || null,
        descricao: campos.descricao.trim() || null,
      })
      .eq("id", documento.id);
    setGravando(false);
    if (error) {
      notify(`Não foi possível gravar: ${error.message}`, { type: "error" });
      return;
    }
    notify("Documento atualizado.", { type: "success" });
    refresh();
    aoFechar();
  }

  // Exclusão (só admin; a política do banco também exige admin): apaga o registro e depois o arquivo guardado.
  async function excluir() {
    if (!documento) return;
    setGravando(true);
    const { error } = await supabase.from("documento_habilitacao").delete().eq("id", documento.id);
    if (error) {
      setGravando(false);
      notify(`Não foi possível excluir: ${error.message}`, { type: "error" });
      return;
    }
    const { error: erroArquivo } = await supabase.storage.from(BUCKET).remove([documento.arquivo_caminho]);
    setGravando(false);
    notify(erroArquivo ? `Documento excluído; o arquivo guardado não pôde ser removido (${erroArquivo.message}).` : "Documento excluído.", {
      type: erroArquivo ? "warning" : "success",
    });
    refresh();
    aoFechar();
  }

  return (
    <Dialog open onClose={gravando ? undefined : aoFechar} fullWidth maxWidth="sm">
      <DialogTitle>Editar documento</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Button
            size="small"
            startIcon={<OpenIcon />}
            onClick={() => void abrirArquivo(documento.arquivo_caminho)}
            sx={{ alignSelf: "flex-start", textTransform: "none" }}
          >
            {documento.arquivo_nome ?? "Abrir arquivo"}
          </Button>
          <TextFieldMui select label="Tipo de documento" value={campos.tipo_id} onChange={(e) => setCampos((c) => ({ ...c, tipo_id: e.target.value }))} fullWidth>
            {GRUPOS.map((g) => [
              <MenuItem key={g.id} disabled sx={{ fontWeight: 700, opacity: "1 !important" }}>
                {g.name}
              </MenuItem>,
              ...tipos
                .filter((t) => t.grupo === g.id && (t.ativo || t.id === campos.tipo_id))
                .map((t) => (
                  <MenuItem key={t.id} value={t.id} sx={{ pl: 3 }}>
                    {t.nome}
                  </MenuItem>
                )),
            ])}
          </TextFieldMui>
          <Stack direction="row" spacing={2}>
            <TextFieldMui
              type="date"
              label="Emissão"
              value={campos.emissao}
              onChange={(e) => setCampos((c) => ({ ...c, emissao: e.target.value }))}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <TextFieldMui
              type="date"
              label="Validade"
              value={campos.validade}
              onChange={(e) => setCampos((c) => ({ ...c, validade: e.target.value }))}
              fullWidth
              InputLabelProps={{ shrink: true }}
              error={datasInvalidas}
              helperText={datasInvalidas ? "A validade não pode ser anterior à emissão." : tipo && !tipo.tem_validade ? "Este tipo normalmente não tem validade." : undefined}
            />
          </Stack>
          <TextFieldMui label="Descrição (opcional)" value={campos.descricao} onChange={(e) => setCampos((c) => ({ ...c, descricao: e.target.value }))} fullWidth />
        </Stack>
      </DialogContent>
      <DialogActions>
        {admin &&
          (confirmarExclusao ? (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mr: "auto", pl: 1 }}>
              <Typography variant="caption" color="error">
                Excluir este documento e o arquivo? Não pode ser desfeito.
              </Typography>
              <Button color="error" variant="contained" size="small" onClick={() => void excluir()} disabled={gravando}>
                Excluir
              </Button>
              <Button size="small" onClick={() => setConfirmarExclusao(false)} disabled={gravando}>
                Não
              </Button>
            </Stack>
          ) : (
            <Button color="error" startIcon={<DeleteIcon />} onClick={() => setConfirmarExclusao(true)} disabled={gravando} sx={{ mr: "auto" }}>
              Excluir
            </Button>
          ))}
        <Button onClick={aoFechar} disabled={gravando}>
          Cancelar
        </Button>
        <Button variant="contained" onClick={() => void gravar()} disabled={gravando || !campos.tipo_id || datasInvalidas || confirmarExclusao}>
          {gravando ? "Gravando…" : "Gravar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function AlertaIncompleto({ faltam }: { faltam: string[] }) {
  if (faltam.length === 0) return null;
  return (
    <Tooltip title={`Enviado sem ${faltam.join(" e ")}. Use Editar para completar.`}>
      <IncompletoIcon fontSize="small" color="warning" sx={{ verticalAlign: "middle", ml: 0.5 }} aria-label="Dados incompletos" />
    </Tooltip>
  );
}

function LinhaTipo({
  tipo,
  documentos,
  aoRenovar,
  aoEditar,
}: {
  tipo: TipoDocumento;
  documentos: DocumentoVista[];
  aoRenovar: () => void;
  aoEditar: (d: DocumentoVista) => void;
}) {
  const [historico, setHistorico] = useState(false);
  const atual = documentos.find((d) => d.atual) ?? null;
  const anteriores = documentos.filter((d) => !d.atual);
  return (
    <>
      <TableRow>
        <TableCell>
          <Typography variant="body2" fontWeight={600}>
            {tipo.nome}
            {atual && <AlertaIncompleto faltam={dadosFaltantes(atual, tipo)} />}
          </Typography>
          {atual?.descricao && (
            <Typography variant="caption" color="text.secondary">
              {atual.descricao}
            </Typography>
          )}
        </TableCell>
        <TableCell>
          {atual ? (
            <ChipSituacaoDocumento d={atual} />
          ) : tipo.obrigatorio ? (
            <Chip size="small" color="error" icon={<FaltanteIcon />} label="Faltante" />
          ) : (
            <Chip size="small" variant="outlined" label="Não cadastrado" />
          )}
        </TableCell>
        <TableCell>{atual ? formatarData(atual.emissao) : "—"}</TableCell>
        <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
          {atual && (
            <Tooltip title="Abrir documento">
              <IconButton size="small" onClick={() => void abrirArquivo(atual.arquivo_caminho)} aria-label="Abrir documento">
                <OpenIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {atual && (
            <Tooltip title="Editar emissão, validade ou tipo">
              <IconButton size="small" onClick={() => aoEditar(atual)} aria-label="Editar documento">
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {anteriores.length > 0 && (
            <Tooltip title={`Histórico (${anteriores.length})`}>
              <IconButton size="small" onClick={() => setHistorico((h) => !h)} aria-label="Histórico">
                <HistoryIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Button size="small" startIcon={atual ? <AutorenewIcon /> : <AddIcon />} onClick={aoRenovar}>
            {atual ? "Renovar" : "Enviar"}
          </Button>
        </TableCell>
      </TableRow>
      {anteriores.length > 0 && (
        <TableRow>
          <TableCell colSpan={4} sx={{ py: 0, borderBottom: historico ? undefined : "none" }}>
            <Collapse in={historico}>
              <Stack spacing={0.5} sx={{ py: 1, pl: 2 }}>
                {anteriores.map((d) => (
                  <Stack key={d.id} direction="row" spacing={1} alignItems="center">
                    <Typography variant="caption" color="text.secondary">
                      Emissão {formatarData(d.emissao)} · validade {formatarData(d.validade)}
                    </Typography>
                    <Button size="small" onClick={() => void abrirArquivo(d.arquivo_caminho)}>
                      Abrir
                    </Button>
                    <Button size="small" onClick={() => aoEditar(d)}>
                      Editar
                    </Button>
                  </Stack>
                ))}
              </Stack>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

/** Tela "Documentos de habilitação": o pacote atual, por grupo. */
export function DocumentosHabilitacao() {
  const [lote, setLote] = useState(false);
  const [envio, setEnvio] = useState<TipoDocumento | "novo" | null>(null);
  const [edicao, setEdicao] = useState<DocumentoVista | null>(null);
  const { data: tipos = [] } = useGetList<TipoDocumento>("tipo_documento", {
    pagination: { page: 1, perPage: 500 },
    sort: { field: "nome", order: "ASC" },
  });
  const { data: documentos = [] } = useGetList<DocumentoVista>("v_documento_habilitacao", {
    pagination: { page: 1, perPage: 2000 },
    sort: { field: "emissao", order: "DESC" },
  });
  const porTipo = useMemo(() => {
    const m = new Map<string, DocumentoVista[]>();
    for (const d of documentos) m.set(d.tipo_id, [...(m.get(d.tipo_id) ?? []), d]);
    return m;
  }, [documentos]);
  const atuais = documentos.filter((d) => d.atual);
  const vencidos = atuais.filter((d) => d.situacao === "vencido").length;
  const vencendo = atuais.filter((d) => d.situacao === "vence_15_dias" || d.situacao === "vence_3_dias").length;
  const faltantes = tipos.filter((t) => t.ativo && t.obrigatorio && !porTipo.has(t.id)).length;
  const incompletos = atuais.filter((d) => {
    const t = tipos.find((x) => x.id === d.tipo_id);
    return t ? dadosFaltantes(d, t).length > 0 : false;
  }).length;

  return (
    <Box sx={{ mt: 2 }}>
      <Title title="Documentos de habilitação" />
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="space-between" sx={{ mb: 2 }}>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip color={vencidos ? "error" : "default"} variant={vencidos ? "filled" : "outlined"} label={`${vencidos} vencido(s)`} />
          <Chip color={vencendo ? "warning" : "default"} variant={vencendo ? "filled" : "outlined"} label={`${vencendo} vencendo em até 15 dias`} />
          <Chip color={faltantes ? "error" : "default"} variant={faltantes ? "filled" : "outlined"} label={`${faltantes} faltante(s)`} />
          <Chip color={incompletos ? "warning" : "default"} variant="outlined" label={`${incompletos} com dados a completar`} />
          <Chip variant="outlined" label={`${atuais.length} documento(s) atual(is)`} />
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setEnvio("novo")}>
            Adicionar documento
          </Button>
          <Button variant="contained" startIcon={<UploadIcon />} onClick={() => setLote(true)}>
            Enviar em lote
          </Button>
        </Stack>
      </Stack>
      <Stack spacing={2}>
        {GRUPOS.map((g) => {
          const tiposDoGrupo = tipos.filter((t) => t.grupo === g.id && (t.ativo || porTipo.has(t.id)));
          if (tiposDoGrupo.length === 0) return null;
          return (
            <Card key={g.id}>
              <CardContent>
                <Typography variant="h6" sx={{ fontSize: "1rem", mb: 1 }}>
                  {g.name}
                </Typography>
                <TableContainer sx={{ overflowX: "auto" }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Documento</TableCell>
                        <TableCell>Situação</TableCell>
                        <TableCell>Emissão</TableCell>
                        <TableCell />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {tiposDoGrupo.map((t) => (
                        <LinhaTipo key={t.id} tipo={t} documentos={porTipo.get(t.id) ?? []} aoRenovar={() => setEnvio(t)} aoEditar={setEdicao} />
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          );
        })}
      </Stack>
      <DialogoLote aberto={lote} aoFechar={() => setLote(false)} tipos={tipos} documentos={documentos} />
      <DialogoDocumento tipo={envio} aoFechar={() => setEnvio(null)} tipos={tipos} />
      <DialogoEditarDocumento documento={edicao} tipos={tipos} aoFechar={() => setEdicao(null)} />
    </Box>
  );
}

/**
 * Parâmetros dos tipos de documento (tela "Parâmetros do sistema"): obrigatório (alerta de faltante, D67),
 * tem validade (alerta de dado a completar) e ativo. Cada mudança grava na hora e fica na auditoria.
 */
const ROTULO_CAMPO_TIPO = { obrigatorio: "Obrigatório", tem_validade: "Com validade", ativo: "Ativo" } as const;

export function TiposDocumentoParametro() {
  const notify = useNotify();
  const { data: tipos = [], refetch } = useGetList<TipoDocumento>("tipo_documento", {
    pagination: { page: 1, perPage: 500 },
    sort: { field: "nome", order: "ASC" },
  });
  const [gravando, setGravando] = useState<string | null>(null);

  async function alternar(t: TipoDocumento, campo: "obrigatorio" | "tem_validade" | "ativo", valor: boolean) {
    setGravando(`${t.id}-${campo}`);
    const { error } = await supabase.from("tipo_documento").update({ [campo]: valor }).eq("id", t.id);
    setGravando(null);
    if (error) notify(`Não foi possível gravar: ${error.message}`, { type: "error" });
    else void refetch();
  }

  return (
    <Card sx={{ mt: 2 }}>
      <CardContent>
        <Typography variant="h6" sx={{ fontSize: "1rem" }}>
          Tipos de documento de habilitação
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Obrigatório: sem documento enviado, aparece como faltante. Com validade: sem a data de validade, aparece como dado a
          completar.
        </Typography>
        <TableContainer sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Tipo</TableCell>
                <TableCell>Grupo</TableCell>
                <TableCell align="center">Obrigatório</TableCell>
                <TableCell align="center">Com validade</TableCell>
                <TableCell align="center">Ativo</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {GRUPOS.flatMap((g) =>
                tipos
                  .filter((t) => t.grupo === g.id)
                  .map((t) => (
                    <TableRow key={t.id} sx={{ opacity: t.ativo ? 1 : 0.5 }}>
                      <TableCell>{t.nome}</TableCell>
                      <TableCell>{g.name}</TableCell>
                      {(["obrigatorio", "tem_validade", "ativo"] as const).map((campo) => (
                        <TableCell key={campo} align="center">
                          <Switch
                            size="small"
                            checked={t[campo]}
                            disabled={gravando !== null}
                            onChange={(e) => void alternar(t, campo, e.target.checked)}
                            slotProps={{ input: { "aria-label": `${ROTULO_CAMPO_TIPO[campo]}: ${t.nome}` } }}
                          />
                        </TableCell>
                      ))}
                    </TableRow>
                  )),
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
}

/* ------------------------------------- Atestados ------------------------------------- */

export const SITUACOES_ATESTADO = [
  { id: "a_solicitar", name: "A solicitar" },
  { id: "solicitado", name: "Solicitado" },
  { id: "recebido", name: "Recebido" },
];

const TIPOS_ANEXO = [
  { id: "requerimento", name: "Requerimento" },
  { id: "nota_fiscal", name: "Nota fiscal" },
  { id: "proposta", name: "Proposta" },
  { id: "ata_contrato", name: "Ata ou contrato" },
  { id: "outro", name: "Outro" },
];

function ChipAtestado({ situacao }: { situacao: string }) {
  if (situacao === "recebido") return <Chip size="small" color="success" variant="outlined" label="Recebido" />;
  if (situacao === "solicitado") return <Chip size="small" color="warning" label="Solicitado" />;
  return <Chip size="small" variant="outlined" label="A solicitar" />;
}

const filtrosAtestado = [
  <TextInput key="busca" source="busca@ilike" label="Pesquisar objeto" parse={normalizarBusca} alwaysOn resettable />,
  <SelectInput key="situacao" source="situacao" label="Situação" choices={SITUACOES_ATESTADO} alwaysOn />,
  <ReferenceInput key="pessoa" source="pessoa_id" reference="pessoa" sort={{ field: "nome", order: "ASC" }}>
    <AutocompleteInput optionText="nome" label="Órgão emissor" filterToQuery={(t: string) => (t ? { "busca@ilike": normalizarBusca(t) } : {})} />
  </ReferenceInput>,
];

/** Objeto sugerido a partir do nome do arquivo (remove numeração, "Atestado de capacidade técnica", nome da empresa). */
function objetoDoNome(nome: string): string {
  return nome
    .replace(/\.[a-z0-9]+$/i, "")
    .replace(/^\s*\d+\s*[.\-_)]?\s*/, "")
    .replace(/atest[ao](do)?\s*(de\s*)?(capacidade)?\s*(t[ée]cnica)?/gi, "")
    .replace(/\bACT\b/g, "")
    .replace(/sobral\s*nunes(\s*(com(ercio)?|ltda|e|tecnologia|\.))*/gi, "")
    .replace(/[_]+/g, " ")
    .replace(/^[\s\-–.]+|[\s\-–.]+$/g, "")
    .replace(/\s{2,}/g, " ")
    .trim();
}

interface LinhaAtestado {
  chave: string;
  arquivo: File;
  pessoa: { id: string; nome: string } | null;
  cnpjSugerido: string | null;
  objeto: string;
  data_emissao: string;
  lendo: boolean;
  enviar: boolean;
}

/** Escolha de órgão emissor com busca (para as linhas do lote). */
function EscolhaOrgao({
  valor,
  aoMudar,
  erro = false,
}: {
  valor: { id: string; nome: string } | null;
  aoMudar: (p: { id: string; nome: string } | null) => void;
  erro?: boolean;
}) {
  const [texto, setTexto] = useState("");
  const { data: opcoes = [] } = useGetList<{ id: string; nome: string }>("pessoa", {
    filter: texto ? { "busca@ilike": normalizarBusca(texto) } : {},
    pagination: { page: 1, perPage: 20 },
    sort: { field: "nome", order: "ASC" },
  });
  return (
    <Autocomplete
      size="small"
      value={valor}
      options={opcoes}
      filterOptions={(x) => x}
      getOptionLabel={(o) => o.nome}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      onInputChange={(_, v, motivo) => motivo === "input" && setTexto(v)}
      onChange={(_, v) => aoMudar(v)}
      noOptionsText="Nenhum órgão encontrado"
      renderInput={(params) => <TextFieldMui {...params} placeholder="Buscar órgão" error={erro} />}
    />
  );
}

/** Envio de vários atestados: órgão pelo CNPJ do PDF (cadastro automático se não existir), objeto e data sugeridos. */
function DialogoLoteAtestados({ aberto, aoFechar }: { aberto: boolean; aoFechar: () => void }) {
  const notify = useNotify();
  const refresh = useRefresh();
  const celular = useCelular();
  const [linhas, setLinhas] = useState<LinhaAtestado[]>([]);
  const [gravando, setGravando] = useState(false);
  const [cadastrando, setCadastrando] = useState<string | null>(null);
  // Cadastro manual do órgão de uma linha (quando o CNPJ não foi lido ou a consulta à Receita falhou).
  const [cadastroManual, setCadastroManual] = useState<{ chave: string; cnpj: string | null } | null>(null);

  const alterar = (chave: string, campos: Partial<LinhaAtestado>) =>
    setLinhas((ls) => ls.map((l) => (l.chave === chave ? { ...l, ...campos } : l)));

  async function receber(lista: FileList | null) {
    if (!lista) return;
    const { data: empresa } = await supabase.from("empresa").select("cnpj").maybeSingle();
    const nosso = (empresa?.cnpj as string | undefined) ?? "";
    const novas: LinhaAtestado[] = Array.from(lista)
      .filter((a) => TIPOS_ACEITOS.includes(a.type))
      .map((arquivo) => ({
        chave: `${arquivo.name}-${Math.random().toString(36).slice(2, 8)}`,
        arquivo,
        pessoa: null,
        cnpjSugerido: null,
        objeto: objetoDoNome(arquivo.name),
        data_emissao: "",
        lendo: true,
        enviar: true,
      }));
    setLinhas((atuais) => [...atuais, ...novas]);
    for (const l of novas) {
      const texto = await textoDoPdf(l.arquivo);
      const cnpj = extrairCnpjs(texto).find((c) => c !== nosso) ?? null;
      let pessoa: { id: string; nome: string } | null = null;
      if (cnpj) {
        const { data } = await supabase.from("pessoa").select("id, nome").eq("documento", cnpj).maybeSingle();
        pessoa = (data as { id: string; nome: string } | null) ?? null;
      }
      alterar(l.chave, { cnpjSugerido: cnpj, pessoa, data_emissao: (texto && extrairDataAtestado(texto)) || "", lendo: false });
    }
  }

  async function cadastrarOrgao(l: LinhaAtestado) {
    if (!l.cnpjSugerido) return;
    setCadastrando(l.chave);
    try {
      const pessoa = await criarPessoaPorCnpj(l.cnpjSugerido);
      // O mesmo órgão pode emitir vários atestados da lista.
      setLinhas((ls) => ls.map((x) => (x.cnpjSugerido === l.cnpjSugerido ? { ...x, pessoa } : x)));
      notify(`Órgão "${pessoa.nome}" cadastrado.`, { type: "success" });
    } catch (e) {
      notify(`Cadastro automático indisponível (${(e as Error).message}). Complete o cadastro manualmente.`, { type: "warning" });
      setCadastroManual({ chave: l.chave, cnpj: l.cnpjSugerido });
    } finally {
      setCadastrando(null);
    }
  }

  function usarOrgaoCadastrado(pessoa: { id: string; nome: string }) {
    if (!cadastroManual) return;
    const { chave, cnpj } = cadastroManual;
    setLinhas((ls) => ls.map((x) => (x.chave === chave || (cnpj && x.cnpjSugerido === cnpj && !x.pessoa) ? { ...x, pessoa } : x)));
    setCadastroManual(null);
  }

  // Grava só as linhas prontas (órgão e objeto informados); as pendentes ficam na lista para completar.
  const pronta = (l: LinhaAtestado) => l.enviar && !l.lendo && Boolean(l.pessoa) && Boolean(l.objeto.trim());
  async function gravar() {
    const selecionadas = linhas.filter(pronta);
    const pendentes = linhas.filter((l) => l.enviar && !pronta(l)).length;
    if (selecionadas.length === 0) return;
    setGravando(true);
    let feitos = 0;
    const falhas: string[] = [];
    for (const l of selecionadas) {
      try {
        const caminho = await enviarArquivo("atestados", l.arquivo);
        const { error } = await supabase.from("atestado_capacidade").insert({
          pessoa_id: l.pessoa?.id,
          objeto: l.objeto.trim(),
          situacao: "recebido",
          data_emissao: l.data_emissao || null,
          arquivo_caminho: caminho,
          arquivo_nome: l.arquivo.name,
        });
        if (error) throw new Error(error.message);
        feitos += 1;
        setLinhas((ls) => ls.filter((x) => x.chave !== l.chave));
      } catch (e) {
        falhas.push(`${l.arquivo.name}: ${(e as Error).message}`);
      }
    }
    setGravando(false);
    const aviso = [
      `${feitos} atestado(s) gravado(s).`,
      pendentes ? `${pendentes} pendente(s) continuam na lista para completar.` : "",
      falhas.length ? `Não gravados: ${falhas.join("; ")}` : "",
    ].filter(Boolean);
    notify(aviso.join(" "), { type: falhas.length || pendentes ? "warning" : "success" });
    if (feitos > 0) refresh();
    if (falhas.length === 0 && pendentes === 0) aoFechar();
  }

  const marcadas = linhas.filter(pronta).length;
  const pendentesLista = linhas.filter((l) => l.enviar && !l.lendo && !pronta(l)).length;
  return (
    <Dialog open={aberto} onClose={gravando ? undefined : aoFechar} fullWidth maxWidth="xl" fullScreen={celular}>
      <DialogTitle>Enviar atestados em lote</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Alert severity="info">
            Os atestados entram como "recebidos". O órgão emissor é identificado pelo CNPJ que aparece no PDF; se ainda não estiver
            cadastrado, use "Cadastrar pelo CNPJ". Objeto e data são sugeridos pelo nome do arquivo e pelo texto: confira cada linha.
          </Alert>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button component="label" variant="outlined" startIcon={<UploadIcon />} disabled={gravando}>
              Selecionar arquivos
              <input type="file" hidden multiple accept={TIPOS_ACEITOS.join(",")} onChange={(e) => void receber(e.target.files)} />
            </Button>
            <Button component="label" variant="outlined" startIcon={<FolderIcon />} disabled={gravando}>
              Selecionar pasta
              <input type="file" hidden multiple onChange={(e) => void receber(e.target.files)} {...({ webkitdirectory: "", directory: "" } as Record<string, string>)} />
            </Button>
          </Stack>
          {linhas.length > 0 && (
            <TableContainer sx={{ overflowX: "auto" }}>
              <Table size="small" sx={{ tableLayout: "fixed", minWidth: 1000 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 56 }}>Enviar</TableCell>
                    <TableCell sx={{ width: 220 }}>Arquivo</TableCell>
                    <TableCell sx={{ width: 300 }}>Órgão emissor</TableCell>
                    <TableCell>Objeto</TableCell>
                    <TableCell sx={{ width: 165 }}>Emissão</TableCell>
                    <TableCell sx={{ width: 48 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {linhas.map((l) => (
                    <TableRow key={l.chave} sx={{ opacity: l.enviar ? 1 : 0.5, verticalAlign: "top" }}>
                      <TableCell>
                        <input type="checkbox" checked={l.enviar} onChange={(e) => alterar(l.chave, { enviar: e.target.checked })} aria-label="Enviar" />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0 }}>
                          <BotaoVerArquivo arquivo={l.arquivo} />
                          <Tooltip title={l.arquivo.name}>
                            <Typography variant="body2" noWrap>
                              {l.arquivo.name}
                            </Typography>
                          </Tooltip>
                        </Stack>
                        {l.lendo && (
                          <Typography variant="caption" color="text.secondary">
                            Lendo o PDF…
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <EscolhaOrgao valor={l.pessoa} aoMudar={(p) => alterar(l.chave, { pessoa: p })} erro={l.enviar && !l.lendo && !l.pessoa} />
                        {!l.pessoa && !l.lendo && (
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                            {l.cnpjSugerido && (
                              <Button size="small" onClick={() => void cadastrarOrgao(l)} disabled={cadastrando !== null}>
                                {cadastrando === l.chave ? "Consultando a Receita…" : `Cadastrar pelo CNPJ ${formatarCnpj(l.cnpjSugerido)}`}
                              </Button>
                            )}
                            <Button size="small" startIcon={<AddIcon />} onClick={() => setCadastroManual({ chave: l.chave, cnpj: l.cnpjSugerido })}>
                              {l.cnpjSugerido ? "Cadastrar manualmente" : "Cadastrar órgão"}
                            </Button>
                          </Stack>
                        )}
                        {!l.pessoa && !l.cnpjSugerido && !l.lendo && (
                          <Typography variant="caption" color="warning.main" display="block">
                            CNPJ não encontrado no PDF: escolha ou cadastre o órgão.
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <TextFieldMui size="small" value={l.objeto} onChange={(e) => alterar(l.chave, { objeto: e.target.value })} fullWidth multiline error={l.enviar && !l.objeto.trim()} />
                      </TableCell>
                      <TableCell>
                        <TextFieldMui type="date" size="small" value={l.data_emissao} onChange={(e) => alterar(l.chave, { data_emissao: e.target.value })} fullWidth />
                      </TableCell>
                      <TableCell>
                        <IconButton size="small" onClick={() => setLinhas((ls) => ls.filter((x) => x.chave !== l.chave))} aria-label="Remover da lista">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Typography variant="caption" sx={{ mr: "auto", pl: 2 }}>
          {linhas.length > 0 ? `${linhas.length} arquivo(s) na lista${pendentesLista ? ` · ${pendentesLista} pendente(s) (ficam na lista)` : ""}` : ""}
        </Typography>
        <Button onClick={aoFechar} disabled={gravando}>
          Cancelar
        </Button>
        <Button variant="contained" onClick={() => void gravar()} disabled={gravando || marcadas === 0}>
          {gravando ? "Gravando…" : `Gravar ${marcadas} pronto(s)`}
        </Button>
      </DialogActions>
      {cadastroManual && (
        <DialogoCadastroPessoa documentoInicial={cadastroManual.cnpj} aoCancelar={() => setCadastroManual(null)} aoCriar={usarOrgaoCadastrado} />
      )}
    </Dialog>
  );
}

function AcoesAtestados() {
  const [lote, setLote] = useState(false);
  return (
    <TopToolbar>
      <Button size="small" startIcon={<UploadIcon />} onClick={() => setLote(true)}>
        Enviar em lote
      </Button>
      <CreateButton />
      <DialogoLoteAtestados aberto={lote} aoFechar={() => setLote(false)} />
    </TopToolbar>
  );
}

export function AtestadoLista() {
  const celular = useCelular();
  return (
    <List title="Atestados de capacidade técnica" sort={{ field: "data_emissao", order: "DESC" }} filters={filtrosAtestado} actions={<AcoesAtestados />} exporter={false}>
      {celular ? (
        <SimpleList
          primaryText={(r) => r.objeto}
          secondaryText={(r) => (
            <ReferenceField record={r} source="pessoa_id" reference="pessoa" link={false}>
              <TextField source="nome" />
            </ReferenceField>
          )}
          tertiaryText={(r) => <ChipAtestado situacao={r.situacao} />}
          linkType="edit"
        />
      ) : (
        <Datagrid bulkActionButtons={false} rowClick="edit">
          <ReferenceField source="pessoa_id" reference="pessoa" link={false}>
            <TextField source="nome" />
          </ReferenceField>
          <TextField source="objeto" />
          <FunctionField source="situacao" render={(r) => <ChipAtestado situacao={r.situacao} />} />
          <FunctionField source="data_solicitacao" render={(r) => formatarData(r.data_solicitacao)} />
          <FunctionField source="data_emissao" render={(r) => formatarData(r.data_emissao)} />
          <FunctionField
            label=""
            render={(r) =>
              r.arquivo_caminho ? (
                <Button
                  size="small"
                  startIcon={<OpenIcon />}
                  onClick={(e) => {
                    e.stopPropagation();
                    void abrirArquivo(r.arquivo_caminho as string);
                  }}
                >
                  Abrir
                </Button>
              ) : null
            }
          />
        </Datagrid>
      )}
    </List>
  );
}

/** Arquivo do atestado (PDF), enviado ao bucket privado. */
function CampoArquivoAtestado() {
  const { field } = useInput({ source: "arquivo_caminho" });
  const { field: campoNome } = useInput({ source: "arquivo_nome" });
  const notify = useNotify();
  const [enviando, setEnviando] = useState(false);
  const caminho = (field.value as string | null) ?? null;
  return (
    <Stack spacing={1} sx={{ width: "100%" }}>
      <Typography variant="subtitle2" color="text.secondary">
        Atestado (PDF) — obrigatório quando recebido
      </Typography>
      <ZonaArquivo
        aceitos={TIPOS_ACEITOS}
        rotulo="Arquivo do atestado"
        enviando={enviando}
        alturaMinima={100}
        aoReceber={(a) => {
          setEnviando(true);
          enviarArquivo("atestados", a)
            .then((c) => {
              field.onChange(c);
              campoNome.onChange(a.name);
              notify("Arquivo anexado. Salve para gravar.", { type: "info" });
            })
            .catch((e: Error) => notify(`Falha ao enviar: ${e.message}`, { type: "error" }))
            .finally(() => setEnviando(false));
        }}
      >
        {caminho && (
          <Button size="small" startIcon={<OpenIcon />} onClick={(e) => { e.stopPropagation(); void abrirArquivo(caminho); }}>
            {(campoNome.value as string | null) ?? "Abrir atestado"}
          </Button>
        )}
      </ZonaArquivo>
    </Stack>
  );
}

function CamposAtestado() {
  return (
    <Grid container columnSpacing={2} sx={{ width: "100%" }}>
      <Grid size={{ xs: 12, md: 8 }}>
        <ReferenceInput source="pessoa_id" reference="pessoa" sort={{ field: "nome", order: "ASC" }} perPage={25}>
          <AutocompleteInput
            optionText="nome"
            label="Órgão emissor"
            validate={required()}
            fullWidth
            filterToQuery={(t: string) => (t ? { "busca@ilike": normalizarBusca(t) } : {})}
            create={<CadastroRapidoPessoa />}
            createItemLabel='Cadastrar órgão "%{item}"'
            createLabel="Cadastrar novo órgão"
          />
        </ReferenceInput>
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <SelectInput source="situacao" choices={SITUACOES_ATESTADO} validate={required()} fullWidth />
      </Grid>
      <Grid size={12}>
        <TextInput source="objeto" validate={required()} multiline fullWidth helperText="O que foi fornecido (ex.: nobreaks, material de expediente)." />
      </Grid>
      <Grid size={{ xs: 6, md: 3 }}>
        <DateInput source="data_solicitacao" parse={vazioParaNulo} fullWidth />
      </Grid>
      <Grid size={{ xs: 6, md: 3 }}>
        <DateInput source="data_emissao" parse={vazioParaNulo} fullWidth />
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <TextInput source="referencia" parse={vazioParaNulo} fullWidth helperText="Pregão, ata ou contrato de origem." />
      </Grid>
      <Grid size={12}>
        <CampoArquivoAtestado />
      </Grid>
      <Grid size={12}>
        <TextInput source="observacao" parse={vazioParaNulo} multiline fullWidth />
      </Grid>
    </Grid>
  );
}

const validarAtestado = (v: Record<string, unknown>) =>
  v.situacao === "recebido" && !v.arquivo_caminho ? { situacao: "Para marcar como recebido, anexe o atestado." } : {};

export function AtestadoCriar() {
  return (
    <Create title="Novo atestado" redirect="edit">
      <SimpleForm defaultValues={{ situacao: "a_solicitar" }} validate={validarAtestado}>
        <CamposAtestado />
      </SimpleForm>
    </Create>
  );
}

interface Anexo {
  id: string;
  tipo: string;
  arquivo_caminho: string;
  arquivo_nome: string | null;
  criado_em: string;
}

/** Anexos do pedido de atestado: requerimento, notas fiscais, proposta, ata/contrato. */
function AnexosAtestado() {
  const registro = useRecordContext<{ id: string }>();
  const notify = useNotify();
  const refresh = useRefresh();
  const [tipo, setTipo] = useState("nota_fiscal");
  const [enviando, setEnviando] = useState(false);
  const { data: anexos = [] } = useGetList<Anexo>(
    "atestado_anexo",
    { filter: { atestado_id: registro?.id }, pagination: { page: 1, perPage: 100 }, sort: { field: "criado_em", order: "ASC" } },
    { enabled: Boolean(registro?.id) },
  );
  if (!registro) return null;

  async function enviar(arquivo: File) {
    if (!registro) return;
    setEnviando(true);
    try {
      const caminho = await enviarArquivo(`atestados/${registro.id}`, arquivo);
      const { error } = await supabase.from("atestado_anexo").insert({ atestado_id: registro.id, tipo, arquivo_caminho: caminho, arquivo_nome: arquivo.name });
      if (error) throw new Error(error.message);
      notify("Anexo gravado.", { type: "success" });
      refresh();
    } catch (e) {
      notify(`Falha ao anexar: ${(e as Error).message}`, { type: "error" });
    } finally {
      setEnviando(false);
    }
  }

  return (
    <Card sx={{ mt: 2 }}>
      <CardContent>
        <Typography variant="h6" sx={{ fontSize: "1rem", mb: 1 }}>
          Documentos do pedido
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 5 }}>
            <Stack spacing={1}>
              <TextFieldMui select size="small" label="Tipo do anexo" value={tipo} onChange={(e) => setTipo(e.target.value)}>
                {TIPOS_ANEXO.map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    {t.name}
                  </MenuItem>
                ))}
              </TextFieldMui>
              <ZonaArquivo aceitos={TIPOS_ACEITOS} rotulo="Anexar documento ao pedido" enviando={enviando} alturaMinima={90} aoReceber={(a) => void enviar(a)} />
            </Stack>
          </Grid>
          <Grid size={{ xs: 12, md: 7 }}>
            {anexos.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Nenhum documento anexado.
              </Typography>
            ) : (
              <Stack spacing={0.5}>
                {anexos.map((a) => (
                  <Stack key={a.id} direction="row" spacing={1} alignItems="center">
                    <Chip size="small" variant="outlined" label={TIPOS_ANEXO.find((t) => t.id === a.tipo)?.name ?? a.tipo} />
                    <Button size="small" onClick={() => void abrirArquivo(a.arquivo_caminho)} sx={{ textTransform: "none" }}>
                      {a.arquivo_nome ?? "Abrir"}
                    </Button>
                  </Stack>
                ))}
              </Stack>
            )}
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}

export function AtestadoEditar() {
  return (
    <Edit title="Atestado de capacidade técnica" mutationMode="pessimistic" redirect={false}>
      <SimpleForm toolbar={<BarraFormulario />} validate={validarAtestado}>
        <CamposAtestado />
      </SimpleForm>
      <AnexosAtestado />
    </Edit>
  );
}
